import { OperationVariables } from "@apollo/client/core";
import { UseBaseFetchQueryReturnType } from "@flaminem/utils";
import React from "react";

import { SearchParams } from "src/graphql/anti-fraud/queries/typings.ts";
import { ErrorCode } from "src/graphql/utils/errorBuilder.ts";
import { debounce } from "src/utils/functions.utils.ts";

// The type definition of all the search hooks in the app
// Not that [alertStatus] is only used in the alerts search
export type SearchQueryHook<
  TReturn,
  TData,
  TVariables extends OperationVariables = OperationVariables,
  TSearch extends SearchParams = SearchParams,
> = (options: TSearch) => UseBaseFetchQueryReturnType<TReturn, TData, ErrorCode, TVariables>;

export const debounceSearchTime = 300;

export type UseSearchProps<
  TReturn,
  TData,
  TVariables extends OperationVariables = OperationVariables,
  TSearch extends SearchParams = SearchParams,
> = {
  searchQueryHook: SearchQueryHook<TReturn, TData, TVariables>;
  searchParams: TSearch;
};

export const useSearch = <
  TReturn,
  TData,
  TVariables extends OperationVariables = OperationVariables,
  TSearch extends SearchParams = SearchParams,
>({
  searchQueryHook,
  searchParams,
}: UseSearchProps<TReturn, TData, TVariables, TSearch>) => {
  const {
    data: filteredData,
    loading,
    error,
    fetchMore,
    refetch,
    networkStatus,
  } = searchQueryHook({
    ...searchParams,
  });

  const handleOnSearchDebounced = React.useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleOnSearch = (func: () => any) => func();
    // debounce the search by 300ms to avoid too many requests
    return debounce(handleOnSearch, debounceSearchTime);
  }, []);

  return {
    filteredData,
    searchLoading: loading,
    handleOnSearch: handleOnSearchDebounced,
    searchError: error,
    fetchMore,
    refetch,
    networkStatus,
  };
};
